import type { PlayerPlugin } from '@foundation-player/core';
import { CreatePlayerOptions } from '@foundation-player/core';
import { LoaderConfig } from '@foundation-player/types';
import { RemoteConfig } from './configuration/get-remote-config';
import { PackageName } from './package-name';
import { PluginConfigs } from './plugin-configs';

/**
 * The bundle types that can be loaded.
 *
 * @remarks
 * By default, this value is auto-detected according to the current browser.
 *
 * For debugging purposes, you can force a specific bundle type.
 */
export enum BundleType {
  /**
   * Recommended target with support for Chrome 63 and newer.
   */
  ES6 = 'bundle.js',

  /**
   * Recommended if browsers older than Chrome 63 need to be supported.
   *
   * @remarks
   * Support starts with Chrome 43.
   */
  ES5 = 'bundle.es5.js',

  /**
   * ES6 for internal testing
   *
   * @remarks
   * This type is the same as ES6, except it is not minified and includes source maps.
   *
   * This target is only available locally and not on the CDN.
   */
  ES6Debug = 'bundle.debug.js',

  /**
   * ES5 for internal testing
   *
   * @remarks
   * This type is the same as ES5, except it is not minified and includes source maps.
   *
   * This target is only available locally and not on the CDN.
   */
  ES5Debug = 'bundle.es5.debug.js',

  /**
   * For loading stylesheet files
   *
   * @remarks
   * Some of our UI packages, may require a CSS file to be loaded.
   */
  CSS = 'bundle.css',
}

export enum PlayerType {
  Bitmovin = 'player-bitmovin',
  DashJs = 'player-dashjs',
  Native = 'player-native',
  Chromecast = 'player-chromecast',
}

export enum AdPlayerType {
  Bitmovin = 'adplayer-bitmovin',
  Native = 'adplayer-native',
}

/**
 * The builder config, after applying all our transformations to the {@link UserConfig}.
 */
export type BuilderConfig = Pick<Required<CreatePlayerOptions>, 'playerConfig'> & {
  /**
   * The type of player to create.
   */
  playerType: PlayerType;

  /**
   * The type of ad player to create.
   *
   * @remarks
   * This is only relevant if advertising is enabled.
   */
  adPlayerType: AdPlayerType;

  /**
   * Specify which type of bundles to load (ES5 or ES6).
   *
   * @remarks
   * Defaults to ES6 for modern web browsers (Chrome 63 and newer).
   * This is done via a simple feature detection inside the loader, before any bundle is requested.
   *
   * Please note: Bundles are downloaded from a CDN. Therefore, transpilation
   * in the project that imports the `createPlayer()` function have no effect on the
   * bundle code of the player at runtime.
   *
   * @default 'ES6'
   */
  bundleType: BundleType;

  /**
   * All plugin configs
   *
   * @internal
   */
  pluginConfigs: PluginConfigs;

  /**
   * Use local Foundation Player script sources.
   *
   * @remarks
   * This is a legacy feature and should not be used anymore.
   * It is only present for backwards compatibility with old Loader versions.
   * Use the `cdn` option instead.
   *
   * @internal
   */
  isLocal?: boolean;

  /**
   * Use a different cdn host for loading the player.
   *
   * @remarks
   * This is only used for debugging purposes to e.g. enable loading from a local server.
   *
   * @internal
   */
  cdn?: string;

  /**
   * For debugging purposes only you can pass in a remote config. If one is
   * given, the request to load the remote config will be skipped and this one
   * will be used.
   *
   * This feature is meant to be used for reproducing exact settings. For
   * example when a bug occurred with a specific remote config that was
   * changed in the meantime. Then you can pass in the former remote config to
   * be able to reproduce the bug.
   *
   * Do NOT use this for other purposes than debugging.
   *
   * @internal
   */
  remoteConfig: RemoteConfig;
};

export type PluginFunction = () => PlayerPlugin[];

/**
 * Every plugin factory will receive the entire builder config and must return of tuple or undefined.
 * undefined should be returned if no plugin should be created.
 * The tuple contains a list of all packages which should be loaded as well as a function to create the plugin after the bundles were loaded.
 */
export type PluginFactoryFunction = (builderConfig: BuilderConfig) => [PackageName[], PluginFunction] | undefined;

export type PluginFactory = {
  [plugin in keyof PluginConfigs]-?: PluginFactoryFunction;
};

/**
 * The user config is the input for the builder, which was provided by the user,
 * including additional internal options, which are only meant for internal usage.
 */
export type UserConfig = LoaderConfig &
  Pick<CreatePlayerOptions, 'playerConfig'> &
  Pick<BuilderConfig, 'isLocal' | 'cdn' | 'bundleType'> &
  Pick<Partial<BuilderConfig>, 'pluginConfigs' | 'remoteConfig' | 'playerType' | 'adPlayerType'>;
